import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Select,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import {
  EducationSectionSelect,
  EducationSessionSelect,
} from '@/pages/education/components/EducationLookupSelects';

type ApiError = { response?: { data?: { message?: string } } };

type RosterRow = {
  student_id: string;
  admission_no?: string;
  first_name?: string;
  last_name?: string;
  status_code?: string | null;
};

type Register = {
  id: string;
  record_version: number;
  session_id?: string;
  section_id?: string;
  section_name?: string;
  attendance_date?: string;
  period_code?: string;
  status?: string;
  roster?: RosterRow[];
  roster_summary?: { total: number; marked: number; unmarked: number };
};

const STATUS_OPTIONS = [
  { value: 'present', label: 'Present' },
  { value: 'absent', label: 'Absent' },
  { value: 'late', label: 'Late' },
  { value: 'excused', label: 'Excused' },
];

export default function TakeAttendancePage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const back = `/education/attendance?${params.toString()}`;
  const [sessionId, setSessionId] = useState<string | undefined>(() => params.get('session_id') || undefined);
  const [openForm] = Form.useForm();
  const [marks, setMarks] = useState<Record<string, string>>({});

  useEffect(() => {
    if (id) return;
    const qSession = params.get('session_id') || undefined;
    const qSection = params.get('section_id') || undefined;
    const qDate = params.get('date');
    if (qSession) setSessionId(qSession);
    openForm.setFieldsValue({
      session_id: qSession,
      section_id: qSection,
      attendance_date: qDate && dayjs(qDate).isValid() ? dayjs(qDate) : dayjs(),
      period_code: params.get('period_code') || 'daily',
    });
  }, [id, openForm, params]);

  const register = useQuery({
    queryKey: ['education-attendance-register', id],
    enabled: Boolean(id),
    queryFn: () => api.get<Register>(`/education/attendance/registers/${id}`),
  });

  const periodOptions = useQuery({
    queryKey: ['education-attendance-period-options', sessionId],
    enabled: Boolean(sessionId) && !id,
    queryFn: () =>
      api.get<{ options: Array<{ code: string; name: string }> }>('/education/attendance/period-options', {
        params: { session_id: sessionId },
      }),
  });

  const detail = register.data?.data;
  const roster = detail?.roster ?? [];
  const draft = detail?.status === 'draft';

  useEffect(() => {
    if (!detail?.roster) return;
    const next: Record<string, string> = {};
    for (const row of detail.roster) {
      if (row.status_code) next[row.student_id] = row.status_code;
    }
    setMarks(next);
  }, [detail?.roster]);

  const onError = (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' }));

  const open = useMutation({
    mutationFn: (values: {
      session_id: string;
      section_id: string;
      attendance_date: dayjs.Dayjs;
      period_code?: string;
      explicit_reason?: string;
    }) =>
      api.post<{ id: string }>('/education/attendance/registers', {
        session_id: values.session_id,
        section_id: values.section_id,
        attendance_date: values.attendance_date.format('YYYY-MM-DD'),
        period_code: values.period_code || 'daily',
        explicit_reason: values.explicit_reason,
      }),
    onSuccess: (result) => navigate(`/education/attendance/${result.data?.id}?${params.toString()}`),
    onError,
  });

  const save = useMutation({
    mutationFn: () => {
      const payload = Object.entries(marks).map(([student_id, status_code]) => ({ student_id, status_code }));
      return api.post(`/education/attendance/registers/${id}/marks`, {
        record_version: detail?.record_version,
        marks: payload,
      });
    },
    onSuccess: () => {
      message.success(t('attendanceSaved', { defaultValue: 'Attendance saved' }));
      queryClient.invalidateQueries({ queryKey: ['education-attendance-register', id] });
      queryClient.invalidateQueries({ queryKey: ['education-attendance'] });
    },
    onError,
  });

  const submit = useMutation({
    mutationFn: () =>
      api.post(`/education/attendance/registers/${id}/submit`, { record_version: detail?.record_version }),
    onSuccess: () => {
      message.success(t('attendanceSubmitted', { defaultValue: 'Register submitted' }));
      queryClient.invalidateQueries({ queryKey: ['education-attendance-register', id] });
      queryClient.invalidateQueries({ queryKey: ['education-attendance'] });
    },
    onError,
  });

  const lock = useMutation({
    mutationFn: () =>
      api.post(`/education/attendance/registers/${id}/lock`, { record_version: detail?.record_version }),
    onSuccess: () => {
      message.success(t('attendanceLocked', { defaultValue: 'Register locked' }));
      queryClient.invalidateQueries({ queryKey: ['education-attendance-register', id] });
    },
    onError,
  });

  const markAllPresent = () => {
    const next: Record<string, string> = {};
    for (const row of roster) next[row.student_id] = 'present';
    setMarks(next);
  };

  const markedCount = useMemo(() => Object.keys(marks).length, [marks]);

  return (
    <EducationPageChrome
      title={t('takeAttendance')}
      description={t('takeAttendanceDetailIntro', {
        defaultValue:
          'Open a daily register for a section, mark the full roster, then submit. Attendance managers can open any section; teachers only their assigned sections.',
      })}
      tags={detail?.status ? <Tag color={detail.status === 'draft' ? 'orange' : 'green'}>{detail.status}</Tag> : undefined}
      extra={<Button onClick={() => navigate(back)}>{t('backToAttendance')}</Button>}
    >
      <Card size="small" loading={Boolean(id) && register.isLoading}>
        {id ? (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Descriptions size="small" column={{ xs: 1, md: 3 }} bordered>
              <Descriptions.Item label={t('section')}>{detail?.section_name || detail?.section_id || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('attendanceDate')}>{detail?.attendance_date || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('period')}>{detail?.period_code || 'daily'}</Descriptions.Item>
              <Descriptions.Item label={t('roster', { defaultValue: 'Roster' })}>
                {detail?.roster_summary
                  ? `${detail.roster_summary.marked}/${detail.roster_summary.total} ${t('marked', { defaultValue: 'marked' })}`
                  : roster.length}
              </Descriptions.Item>
            </Descriptions>

            {!roster.length ? (
              <Alert
                type="warning"
                showIcon
                message={t('emptyRoster', {
                  defaultValue: 'No students allocated to this section yet. Allocate sections from Enrollment first.',
                })}
              />
            ) : null}

            <Space wrap>
              {draft ? (
                <>
                  <Button onClick={markAllPresent}>{t('markAllPresent', { defaultValue: 'Mark all present' })}</Button>
                  <Button type="primary" loading={save.isPending} onClick={() => save.mutate()} disabled={!markedCount}>
                    {t('saveMarks', { defaultValue: 'Save marks' })} ({markedCount})
                  </Button>
                  <Button loading={submit.isPending} onClick={() => submit.mutate()}>
                    {t('submitRegister', { defaultValue: 'Submit register' })}
                  </Button>
                </>
              ) : null}
              {detail?.status === 'submitted' ? (
                <Button loading={lock.isPending} onClick={() => lock.mutate()}>
                  {t('lockRegister', { defaultValue: 'Lock register' })}
                </Button>
              ) : null}
            </Space>

            <Table
              rowKey="student_id"
              size="small"
              pagination={false}
              dataSource={roster}
              columns={[
                {
                  title: t('admissionNo', { defaultValue: 'Admission no' }),
                  dataIndex: 'admission_no',
                  width: 140,
                },
                {
                  title: t('student'),
                  key: 'name',
                  render: (_: unknown, row: RosterRow) =>
                    [row.first_name, row.last_name].filter(Boolean).join(' ') || '—',
                },
                {
                  title: t('status'),
                  key: 'status',
                  width: 180,
                  render: (_: unknown, row: RosterRow) =>
                    draft ? (
                      <Select
                        style={{ width: '100%' }}
                        options={STATUS_OPTIONS}
                        value={marks[row.student_id]}
                        placeholder={t('status')}
                        onChange={(value) => setMarks((prev) => ({ ...prev, [row.student_id]: value }))}
                      />
                    ) : (
                      <Tag>{row.status_code || '—'}</Tag>
                    ),
                },
              ]}
            />
          </Space>
        ) : (
          <Form
            form={openForm}
            layout="vertical"
            initialValues={{ attendance_date: dayjs(), period_code: 'daily' }}
            onFinish={(values) => open.mutate(values)}
            style={{ maxWidth: 520 }}
          >
            <Form.Item name="session_id" label={t('session')} rules={[{ required: true }]}>
              <EducationSessionSelect
                onChange={(value) => {
                  setSessionId(value);
                  openForm.setFieldValue('section_id', undefined);
                }}
              />
            </Form.Item>
            <Form.Item name="section_id" label={t('section')} rules={[{ required: true }]}>
              <EducationSectionSelect sessionId={sessionId} />
            </Form.Item>
            <Form.Item name="attendance_date" label={t('attendanceDate')} rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
            </Form.Item>
            <Form.Item
              name="period_code"
              label={t('period')}
              extra={t('periodCodeHint', {
                defaultValue: 'Daily is the whole-day register. Other codes come from the session timetable (non-break periods).',
              })}
            >
              <Select
                showSearch
                optionFilterProp="label"
                loading={periodOptions.isLoading}
                placeholder="daily"
                options={(periodOptions.data?.data?.options || [{ code: 'daily', name: 'Daily (whole day)' }]).map(
                  (row) => ({ value: row.code, label: `${row.code} — ${row.name}` })
                )}
              />
            </Form.Item>
            <Form.Item
              name="explicit_reason"
              label={t('reason')}
              extra={t('holidayReasonHint', { defaultValue: 'Required only when marking on a holiday / non-instructional day' })}
            >
              <Input.TextArea rows={2} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={open.isPending}>
              {t('openRegister', { defaultValue: 'Open register' })}
            </Button>
          </Form>
        )}
      </Card>
    </EducationPageChrome>
  );
}
